import type { Instance } from "mobx-state-tree";
import { types, flow, getParent, hasParent } from "mobx-state-tree";
import type {
  Discipline,
  DrawingIssueAnchor,
  PMIssue,
  PMIssueFilters,
  PMIssueComment,
  PMIssueActivity,
  UserProfile,
} from "@/domains/pm/types";
import type { ResyncChannelHandle } from "@/lib/realtime/channel";
import { createCoalescer, type Coalescer } from "@/lib/realtime/coalesce";
import * as pmRepo from "./repository";
import { queryClient } from "@/lib/queryClient";
import { issueDisplayTitle } from "@/domains/pm/issueTitle";
import { PM_ISSUE_LOAD_CEILING, PM_PAGE_SIZE_ISSUES } from "@/domains/pm/constants";
import { track } from "@/lib/posthog";
import {
  correctDraftDefaults,
  draftTitleIsEmpty,
  initialDraftValues,
  newIssueMatchesFilters,
  projectStoredDraft,
  type DraftIssueLookups,
  type IssueDraft,
  type IssueDraftChangeMeta,
  type IssueDraftDefaults,
  type IssueDraftValues,
} from "@/domains/pm/issueDraft";
import type { StagedAttachment } from "@/domains/pm/components/issues/IssueAttachmentsStager";
import { createIssueWithAssociations } from "@/domains/pm/createIssueWithAssociations";

/**
 * Bin a document a discarded draft uploaded. The documents repository is a
 * large module graph the PM store otherwise never needs, so it is loaded only
 * when a draft's upload actually has to go — not with every page that reads
 * issues.
 */
async function binDocument(documentId: number, userProfileId: number): Promise<void> {
  const { deleteDocument } = await import("@/stores/documents/repository/mutations");
  await deleteDocument(String(documentId), String(userProfileId));
}

/** One page as `pmRepo.listIssues` returns it. `count` is the exact size of
 *  the filtered scope on the first page and null on every later one. */
type IssuePage = { data: PMIssue[]; count: number | null };

/**
 * A realtime burst — a bulk status change, a colleague working through a
 * meeting's issues — emits an event per row, and each one asks for the whole
 * scope again. Coalesce them: run on the first, then at most once more per
 * window for as long as events keep arriving.
 */
const ISSUE_RESYNC_WINDOW_MS = 750;

/**
 * Does this write move the issue out of what the current query returns?
 *
 * Status decides whether a row belongs on the page at all (and, under the
 * Overdue filter, so does due_date). Patching one of those in place therefore
 * leaves a row the query would no longer return — a kanban card sitting in a
 * column the filter excludes — and `hiddenByFilterCount`, which is a server
 * count, stale beside it. Only a refetch reconciles both, so the write actions
 * do one rather than leaving each call site to remember.
 */
function writeEscapesFilters(patch: Partial<PMIssue>, filters: PMIssueFilters): boolean {
  return (
    (patch.status !== undefined && !!filters.status?.length) ||
    (patch.due_date !== undefined && !!filters.dueBefore)
  );
}

/** What a caught failure should read as on screen — the thrown message when
 *  there is one, the caller's description of the read when there is not. */
function failureMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** How `commitDraft` ended. It never throws: the page may be gone by the time it settles. */
/**
 * How one `selectIssue` call ended; a later selection supersedes an earlier
 * one. "failed": there is no such issue the caller can see. "unavailable":
 * the read itself failed (network, timeout), so the issue may well exist.
 */
export type SelectIssueOutcome = "opened" | "failed" | "unavailable" | "superseded";

export type DraftCommitResult = { ok: true; issueId: number } | { ok: false; error: unknown };

/** A draft with no title cannot be saved — `pm_issues.title` is what every list shows. */
export class DraftUntitledError extends Error {
  constructor() {
    super("An issue needs a description to be saved");
    this.name = "DraftUntitledError";
  }
}

export interface StartDraftParams {
  buildingId: number;
  /** Recorded now so a commit that outlives the page still knows who filed it. */
  orgId: string | null | undefined;
  userProfileId: number | null | undefined;
  defaults?: IssueDraftDefaults;
}

/**
 * What became of a draft that is gone, for an upload that lands after it: the
 * stager's upload outlives the panel, and its document must still end up
 * linked to the issue the draft became, or binned with the discarded draft.
 */
interface SettledDraft {
  /** The issue it became; null = discarded. */
  issueId: number | null;
  createdBy: number | null;
  /** Documents already accounted for (linked or binned). */
  handled: Set<number>;
}

/**
 * Where a draft finds the people and tags behind its ids: the parent PMStore's
 * disciplines and per-project members cache. Read structurally — importing
 * PMStore here would be a cycle — and absent for a store created on its own
 * (tests), which then projects drafts without chips.
 */
interface DraftLookupHost {
  disciplines: Discipline[];
  projectMembers: { get(projectId: number): UserProfile[] };
}

function isDraftLookupHost(node: unknown): node is DraftLookupHost {
  if (typeof node !== "object" || node === null) return false;
  const host = node as Partial<DraftLookupHost>;
  return Array.isArray(host.disciplines) && typeof host.projectMembers?.get === "function";
}

export const IssuesStore = types
  .model("IssuesStore", {
    /** True until the FIRST page lands. Later pages stream in behind the
     *  rendered list under `isLoadingMore`, so the list is never replaced by a
     *  spinner just because more of it is on the way. */
    isLoading: types.optional(types.boolean, false),
    /** True while the rest of the scope is still being read. */
    isLoadingMore: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    totalCount: types.optional(types.number, 0),
    /** Set when the scope is larger than PM_ISSUE_LOAD_CEILING and the load
     *  stopped short of it. The list says so rather than quietly ending. */
    truncated: types.optional(types.boolean, false),
    // WHA-3452: non-deleted issues in the current scope (project + search)
    // ignoring the IssueFilterControl dimensions — `scopeIssueCount −
    // totalCount` is how many issues the filters hide.
    scopeIssueCount: types.optional(types.number, 0),
  })
  .volatile(() => ({
    issues: [] as PMIssue[],
    /** Monotonic token for scope reads. A filter change, a realtime resync and
     *  a tab switch can all be in flight at once and resolve in any order —
     *  only the newest may write `issues`. It also stops a superseded read
     *  from paging on in the background. */
    loadSeq: 0 as number,
    resyncCoalescer: null as Coalescer | null,
    selectedIssue: null as PMIssue | null,
    selectedIssueComments: [] as PMIssueComment[],
    /** The open issue's thread has not been read yet: the panel opens on the
     *  list row first, and an empty thread must not read as "no comments". */
    isLoadingSelectedComments: false as boolean,
    // WHA-3732: the open issue's change timeline. Loaded lazily when the
    // history dialog opens; cleared with the selection.
    selectedIssueActivity: [] as PMIssueActivity[],
    isLoadingActivity: false as boolean,
    filters: {} as PMIssueFilters,
    /** The scope hiddenByFilterCount measures against. Null = the filters. */
    baselineFilters: null as PMIssueFilters | null,
    channel: null as ResyncChannelHandle | null,
    /** Checklist items anywhere in the building: they move the progress
     *  badges, so they reload the scope through the same coalescer. A channel
     *  of its own, so the issue channel never depends on that table existing. */
    checklistChannel: null as ResyncChannelHandle | null,
    /** "Add checklist" chosen somewhere other than the open panel (the
     *  right-click menu): the panel for this issue opens its checklist
     *  composer when it next renders, then clears this. */
    checklistRequestIssueId: null as number | null,
    // WHA-3339: needed to populate per-issue unread mention counts and
    // to mark mentions read when the user opens an issue. Set by
    // loadIssues; 0 means "skip the unread aggregation".
    currentUserId: 0 as number,
    // The building the loaded scope belongs to. Kept so a write action can
    // refetch it (see writeEscapesFilters) without every caller passing it.
    currentBuildingId: 0 as number,
    // Monotonic request identity: only the newest detail selection may publish
    // its result, and clearSelection invalidates every request still in flight.
    selectionRequestId: 0 as number,
    /**
     * Edits the user has made that the server may not reflect yet, keyed by
     * issue: a write still on the wire, or a title being typed. Every read that
     * replaces rows (a realtime resync, a chatbot refresh, a cascade reload)
     * lays these back over what it read, so a read that raced the write cannot
     * flash the old value back onto the screen.
     */
    pendingPatches: new Map<number, Partial<PMIssue>>(),
    /**
     * New issues being written on /issues, before any of them exists. Their own
     * slot, never `issues`: reloads replace that array wholesale, every write
     * path keys on its ids, and every other reader (parent pickers,
     * dependencies, bulk actions, My Issues) must not see a draft. Views read
     * them through `draftIssuesFor`. Replaced, never mutated in place.
     */
    drafts: [] as IssueDraft[],
    /** The draft open in the panel. At most one; a draft being saved is not active. */
    activeDraftId: null as number | null,
    /** Draft ids count down from -1, so a draft being saved and the next differ. */
    draftSeq: 0 as number,
    /** Commits in flight, so a second leave during a save joins it instead of filing twice. */
    draftCommits: new Map<number, Promise<DraftCommitResult>>(),
    /** Uploads that landed while their draft was being saved — linked once it exists. */
    draftLateUploads: new Map<number, StagedAttachment[]>(),
    /** Drafts saved or discarded, for uploads that land afterwards. See SettledDraft. */
    settledDrafts: new Map<number, SettledDraft>(),
  }))
  .views((self) => ({
    get inProgressIssues() {
      return self.issues.filter((i) => i.status === "in_progress");
    },
    get resolvedIssues() {
      return self.issues.filter((i) => i.status === "resolved");
    },
    get closedIssues() {
      return self.issues.filter((i) => i.status === "closed");
    },
    /**
     * Every loaded issue bucketed by its own status key, including custom
     * ones. A fixed three-key object silently dropped any issue whose project
     * defines its own status, so the kanban lost the card entirely.
     */
    get issuesByStatus(): Record<string, PMIssue[]> {
      const out: Record<string, PMIssue[]> = {
        in_progress: [],
        resolved: [],
        closed: [],
      };
      for (const issue of self.issues) {
        (out[issue.status] ??= []).push(issue);
      }
      return out;
    },
    // WHA-3452: how many issues the IssueFilterControl currently hides.
    get hiddenByFilterCount() {
      return Math.max(0, self.scopeIssueCount - self.totalCount);
    },
  }))
  .views((self) => ({
    /** The draft open in the panel, or null. */
    get activeDraft(): IssueDraft | null {
      return self.drafts.find((d) => d.id === self.activeDraftId) ?? null;
    },
    findDraft(id: number): IssueDraft | null {
      return self.drafts.find((d) => d.id === id) ?? null;
    },
    /** People for the draft's project and every discipline — see DraftLookupHost. */
    draftLookups(projectId: number | null): DraftIssueLookups {
      const host = hasParent(self) ? getParent(self) : null;
      if (!isDraftLookupHost(host)) return { users: [], disciplines: [] };
      return {
        users: projectId != null ? host.projectMembers.get(projectId) : [],
        disciplines: host.disciplines,
      };
    },
  }))
  .views((self) => ({
    /**
     * The drafts filed into one project of a building, as issue rows, newest
     * first, for the List, Kanban and Gantt to draw beside `issues`. A draft
     * belongs to the project it was started in, so a page scoped to another
     * project never draws it. A draft whose issue a reload has already
     * brought in is left out, so the new issue is never drawn twice.
     */
    draftIssuesFor(buildingId: number, projectId: number | null): PMIssue[] {
      if (projectId == null) return [];
      const loaded = new Set(self.issues.map((i) => i.id));
      return self.drafts
        .filter(
          (d) =>
            d.buildingId === buildingId &&
            d.values.projectId === projectId &&
            (d.createdIssueId == null || !loaded.has(d.createdIssueId))
        )
        .sort((a, b) => a.id - b.id)
        .map((d) => projectStoredDraft(d, self.draftLookups(d.values.projectId)));
    },
    /** The active draft as an issue row, or null. */
    get draftIssue(): PMIssue | null {
      const draft = self.activeDraft;
      return draft ? projectStoredDraft(draft, self.draftLookups(draft.values.projectId)) : null;
    },
  }))
  .views((self) => ({
    /** A row read from the server, with the user's unsaved edits laid over it. */
    withPendingEdits<T extends PMIssue>(row: T): T {
      const patch = self.pendingPatches.get(row.id);
      return patch ? { ...row, ...patch } : row;
    },
  }))
  .actions((self) => {
    /**
     * Page through the rest of the scope, appending each page as it lands.
     *
     * Delegated to from the action below rather than inlined, so the paging
     * rules read on their own and the action is left holding only the flags
     * and the error. Every `return` here is "this read is over" — the action's
     * `finally` still runs.
     */
    function* readRemainingPages(
      seq: number,
      buildingId: number
    ): Generator<Promise<IssuePage>, void, IssuePage> {
      // A full page means there may be more; a short one is the end of the
      // scope. Deliberately not `issues.length < totalCount`: the count is a
      // snapshot, and an issue filed while we page would leave that
      // condition true forever.
      let pageSize = self.issues.length;
      while (pageSize === PM_PAGE_SIZE_ISSUES) {
        if (seq !== self.loadSeq) return;
        if (self.issues.length >= PM_ISSUE_LOAD_CEILING) {
          self.truncated = true;
          return;
        }
        self.isLoadingMore = true;
        const cursor = self.issues[self.issues.length - 1].issue_number;
        const next: IssuePage = yield pmRepo.listIssues(
          buildingId,
          self.filters,
          { cursor, limit: PM_PAGE_SIZE_ISSUES },
          self.currentUserId
        );
        if (seq !== self.loadSeq) return;
        pageSize = next.data.length;
        if (pageSize === 0) return;
        self.issues = [...self.issues, ...next.data.map((row) => self.withPendingEdits(row))];
      }
    }

    return {
      /**
       * Read the rest of the scope after `loadIssues` has landed its first page,
       * appending each page as it arrives.
       *
       * Split out of `loadIssues` so that awaiting a load means "the list is on
       * screen", not "every last row is in memory" — the pages gate their first
       * paint and their deep-link handling on that promise. Not for direct use:
       * `seq` is `loadIssues`' own token, and a caller with a stale one would
       * append rows belonging to a scope the user has already left.
       */
      loadRemainingIssues: flow(function* loadRemainingIssues(seq: number, buildingId: number) {
        try {
          yield* readRemainingPages(seq, buildingId);
        } catch (e) {
          if (seq !== self.loadSeq) return;
          self.error = failureMessage(e, "Failed to load issues");
        } finally {
          // A superseded read must not clear a flag the newer one raised.
          if (seq === self.loadSeq) self.isLoadingMore = false;
        }
      }),
    };
  })
  .actions((self) => {
    /**
     * Re-read the open issue in place.
     *
     * Not `selectIssue`: that zeroes the unread count and marks the user's
     * mentions read, which must only happen when the user opens the issue —
     * never because somebody else changed it. Dropped if the selection moves on
     * while the read is out.
     */
    const refreshSelectedIssue = flow(function* refreshSelectedIssue() {
      const current = self.selectedIssue;
      if (!current) return;
      const requestId = self.selectionRequestId;
      try {
        const fresh: PMIssue | null = yield pmRepo.findIssue(current.id);
        if (requestId !== self.selectionRequestId || self.selectedIssue?.id !== current.id) return;
        if (!fresh) {
          // Deleted by someone else: the panel would otherwise go on editing a
          // row that no longer exists.
          self.selectionRequestId += 1;
          self.selectedIssue = null;
          self.selectedIssueComments = [];
          self.selectedIssueActivity = [];
          return;
        }
        self.selectedIssue = {
          ...self.withPendingEdits(fresh),
          unread_mention_count: self.selectedIssue.unread_mention_count,
        };
      } catch (e) {
        // The panel keeps what it shows; the next change event tries again.
        console.error("[pm] Failed to refresh the open issue", current.id, e);
      }
    });
    return { refreshSelectedIssue };
  })
  .actions((self) => ({
    /**
     * Read the current scope: the first page in front, the rest streamed in
     * behind it by `loadRemainingIssues`.
     *
     * The list is grouped, sorted, status-tabbed and laid out as a kanban
     * board entirely client-side, over whatever this array holds. A page left
     * unread is therefore not "page two" — it is an issue that cannot be
     * scrolled to, cannot be sorted to the top, and is missing from its tab's
     * count and its kanban column. So the scope is read to the end, up to
     * PM_ISSUE_LOAD_CEILING.
     *
     * The returned promise settles when the FIRST page is on screen. The
     * background read is deliberately not awaited: a caller that waited for it
     * would hold the loading state over a list it could already show.
     */
    loadIssues: flow(function* loadIssues(buildingId: number, currentUserId = 0) {
      self.currentBuildingId = buildingId;
      const seq = ++self.loadSeq;
      self.isLoading = true;
      self.isLoadingMore = false;
      self.error = null;
      self.truncated = false;
      if (currentUserId) self.currentUserId = currentUserId;
      try {
        const [first, scopeCount]: [IssuePage, number] = yield Promise.all([
          pmRepo.listIssues(
            buildingId,
            self.filters,
            { limit: PM_PAGE_SIZE_ISSUES },
            self.currentUserId
          ),
          // WHA-3452: scope total — project + search + the active status tab
          // (status / dueBefore), but none of the IssueFilterControl
          // dimensions. The tab is a view, not a filter, so it must narrow
          // the baseline exactly as it narrows the list.
          pmRepo.countIssuesInScope(buildingId, self.baselineFilters ?? self.filters),
        ]);
        if (seq !== self.loadSeq) return;
        self.issues = first.data.map((row) => self.withPendingEdits(row));
        self.totalCount = first.count ?? 0;
        self.scopeIssueCount = scopeCount;
        void self.loadRemainingIssues(seq, buildingId);
        // Whatever changed the list may have changed the issue open beside it
        // (a colleague, the chatbot, a cascade from another issue's dates).
        void self.refreshSelectedIssue();
      } catch (e) {
        if (seq !== self.loadSeq) return;
        self.error = failureMessage(e, "Failed to load issues");
      } finally {
        // A superseded read must not clear a flag the newer one raised.
        if (seq === self.loadSeq) self.isLoading = false;
      }
    }),
  }))
  .actions((self) => ({
    /**
     * Re-read just the open thread's comments.
     *
     * Deliberately NOT `selectIssue`: that also zeroes `unread_mention_count`,
     * rewrites the list row and calls `markIssueMentionsRead` — so driving it
     * from a realtime event would mark the current user's mentions read
     * because somebody *else* posted a comment.
     */
    reloadSelectedComments: flow(function* reloadSelectedComments(issueId: number) {
      // The panel may have moved on to another issue (or closed) between the
      // event and this read landing.
      if (self.selectedIssue?.id !== issueId) return;
      try {
        const comments: Awaited<ReturnType<typeof pmRepo.listComments>> =
          yield pmRepo.listComments(issueId);
        if (self.selectedIssue?.id !== issueId) return;
        self.selectedIssueComments = comments;
      } catch (e) {
        // Non-fatal for the thread — it keeps the comments it already has
        // rather than blanking on a transient failure — but a refetch that
        // keeps failing leaves it silently stale, so say so.
        console.error("[pm] Failed to reload issue comments", issueId, e);
      }
    }),

    /** How THIS selection ended: hydrated, failed, or superseded by a later one. */
    // `quiet`: the caller reports a failure itself, so the store's error (the
    // page's banner) is neither set nor cleared — clearing it would also take
    // down an unrelated error the user has not seen yet.
    selectIssue: flow(function* selectIssue(issueId: number, { quiet = false } = {}) {
      const requestId = ++self.selectionRequestId;
      if (!quiet) self.error = null;
      // Open on the row the page already holds, then hydrate behind it. The
      // full read is two chains of several round trips each, and waiting on
      // them left a click with no visible effect for a second or more — long
      // enough that people clicked again, which restarted the wait. A row the
      // page does not hold (a deep link, a parent outside the filter) still
      // waits for the read, since there is nothing to show before it.
      const row = self.issues.find((i) => i.id === issueId);
      if (self.selectedIssue?.id !== issueId) {
        self.selectedIssueComments = [];
        self.selectedIssueActivity = [];
        if (row) self.selectedIssue = { ...row, unread_mention_count: 0 };
      }
      self.isLoadingSelectedComments = true;
      try {
        // WHA-3667: the issue and its comments are independent — fetch both
        // concurrently so the detail modal opens faster.
        const [fresh, comments]: [PMIssue, Awaited<ReturnType<typeof pmRepo.listComments>>] =
          yield Promise.all([pmRepo.getIssue(issueId), pmRepo.listComments(issueId)]);
        if (requestId !== self.selectionRequestId) return "superseded" as SelectIssueOutcome;
        // WHA-3339: snapshot the unread count before we clobber the row
        // so MentionsStore can decrement the nav badge by the right
        // amount without an extra query.
        const prevUnread = row?.unread_mention_count ?? 0;
        const shown = self.withPendingEdits(fresh);
        self.selectedIssue = { ...shown, unread_mention_count: 0 };
        // WHA-3307: keep the list row in sync with the hydrated issue so
        // that edits made in the panel (especially junction-table fields
        // like assignees and disciplines, which don't go through the
        // optimistic-update path) show up immediately in the list.
        // Merged over the row, not swapped for it: getIssue does not hydrate
        // what only the list reads (comment and checklist counts), and a
        // replaced row lost its badges the moment the issue was opened.
        self.issues = self.issues.map((i) =>
          i.id === issueId ? { ...i, ...shown, unread_mention_count: 0 } : i
        );
        self.selectedIssueComments = comments;

        // WHA-3339: mark every unread mention on this issue read for
        // the current user. Best-effort — the UI already cleared the
        // dot optimistically. Persist + emit a window event so the
        // MentionsStore (driving the nav dot) can refresh.
        if (self.currentUserId && prevUnread > 0) {
          try {
            yield pmRepo.markIssueMentionsRead(issueId, self.currentUserId);
            window.dispatchEvent(
              new CustomEvent("franklin:pm-mentions-read", {
                detail: { issueId, count: prevUnread },
              })
            );
          } catch {
            // ignore — non-fatal
          }
        }
        return "opened" as SelectIssueOutcome;
      } catch (e) {
        if (requestId !== self.selectionRequestId) return "superseded" as SelectIssueOutcome;
        // Not message matching: the repository's own error type.
        const missing = e instanceof Error && e.name === "IssueNotFoundError";
        if (!quiet) {
          self.error = failureMessage(e, "Failed to load issue");
        } else if (missing && self.selectedIssue?.id === issueId) {
          // The caller tells the user this issue could not be opened; a panel
          // left open on the list row would say otherwise. A read that merely
          // failed keeps the row: the issue is probably still there.
          self.selectedIssue = null;
          self.selectedIssueComments = [];
          self.selectedIssueActivity = [];
        }
        return (missing ? "failed" : "unavailable") as SelectIssueOutcome;
      } finally {
        if (requestId === self.selectionRequestId) self.isLoadingSelectedComments = false;
      }
    }),

    /** Ask the panel for `issueId` to open its checklist composer. */
    requestChecklist(issueId: number) {
      self.checklistRequestIssueId = issueId;
    },

    clearChecklistRequest() {
      self.checklistRequestIssueId = null;
    },

    clearSelection() {
      self.selectionRequestId += 1;
      self.isLoadingSelectedComments = false;
      self.checklistRequestIssueId = null;
      self.selectedIssue = null;
      self.selectedIssueComments = [];
      self.selectedIssueActivity = [];
    },

    // WHA-3732: load the change timeline for the issue history dialog.
    loadActivity: flow(function* loadActivity(issueId: number) {
      self.isLoadingActivity = true;
      try {
        self.selectedIssueActivity = yield pmRepo.getIssueActivity(issueId);
      } catch (e) {
        self.error = failureMessage(e, "Failed to load issue history");
      } finally {
        self.isLoadingActivity = false;
      }
    }),

    /**
     * `baseline` is the scope the hidden-by-filters count measures against.
     * PMIssues passes project + search only, so ticking a filter box makes the
     * count go UP; My Issues omits it because its tabs are a genuine view axis
     * (dueBefore), and counting against the unfiltered building would inflate
     * every tab's hidden count.
     */
    setFilters(filters: PMIssueFilters, baseline?: PMIssueFilters) {
      self.filters = filters;
      self.baselineFilters = baseline ?? null;
    },

    /**
     * Abandon whatever read is in flight.
     *
     * The background pager walks the whole scope, which on a large building is
     * a long tail of requests. Leaving the page should stop it, not let it run
     * on against a list nobody is looking at. Bumping the token is all it
     * takes — every step checks it.
     */
    cancelLoad() {
      self.loadSeq += 1;
      self.isLoading = false;
      self.isLoadingMore = false;
    },

    createIssue: flow(function* createIssue(
      data: Partial<PMIssue>,
      drawingAnchor?: Pick<DrawingIssueAnchor, "document_id" | "x" | "y">
    ) {
      return drawingAnchor
        ? yield pmRepo.createIssueWithDrawingAnchor(data, drawingAnchor)
        : yield pmRepo.createIssue(data);
    }),

    updateIssue: flow(function* updateIssue(issueId: number, data: Partial<PMIssue>) {
      holdEdit(self.pendingPatches, issueId, data);
      try {
        yield pmRepo.updateIssue(issueId, data);
        // A status or delete on a document sub-issue mirrors onto its comment
        // thread in the database; the comments caches on the Documents page
        // are react-query and would otherwise serve the stale thread until
        // their next realtime signal or stale window.
        if (data.status !== undefined || data.deleted_at !== undefined) {
          void queryClient.invalidateQueries({ queryKey: ["document-comment-threads"] });
          void queryClient.invalidateQueries({ queryKey: ["document-comment-counts"] });
        }
      } finally {
        releaseEdit(self.pendingPatches, issueId, data);
      }
      if (self.currentBuildingId && writeEscapesFilters(data, self.filters)) {
        yield self.loadIssues(self.currentBuildingId);
      }
    }),

    /**
     * Re-derive the display title of every document parent issue for the
     * current language — no refetch, the root and the stored title are
     * already on the row. `BuildingLayout` calls it on a language change.
     */
    relocaliseTitles() {
      const relocalise = (issue: PMIssue): PMIssue =>
        issue.document_root?.auto_title && issue.title_raw !== undefined
          ? { ...issue, title: issueDisplayTitle(issue.title_raw, issue.document_root) }
          : issue;
      self.issues = self.issues.map(relocalise);
      if (self.selectedIssue) self.selectedIssue = relocalise(self.selectedIssue);
    },

    /**
     * Keep `patch` laid over every read of this issue until `releaseEdit`.
     * For a write that does not go through `updateIssue` (the schedule RPC),
     * and for a value still being typed.
     */
    holdEdit(issueId: number, patch: Partial<PMIssue>) {
      holdEdit(self.pendingPatches, issueId, patch);
    },

    /** Stop holding `patch` — only the keys still holding these same values,
     *  so an older write finishing cannot drop a newer one's hold. */
    releaseEdit(issueId: number, patch: Partial<PMIssue>) {
      releaseEdit(self.pendingPatches, issueId, patch);
    },

    /** Stop holding these fields whatever they hold — an abandoned edit. */
    dropEdit(issueId: number, keys: (keyof PMIssue)[]) {
      const held = self.pendingPatches.get(issueId);
      if (!held) return;
      const next = { ...held };
      for (const key of keys) delete next[key];
      if (Object.keys(next).length === 0) self.pendingPatches.delete(issueId);
      else self.pendingPatches.set(issueId, next);
    },

    /**
     * Assignees and disciplines live in junction tables, so a write to one
     * never passes through updateIssue — but it can still move a row out of an
     * active assignee/discipline filter. The call sites ask for this after the
     * junction write instead.
     */
    reconcileJunctionWrite: flow(function* reconcileJunctionWrite(
      dimension: "assignees" | "disciplines"
    ) {
      const narrowed =
        dimension === "assignees"
          ? !!self.filters.assigneeUserIds?.length
          : !!self.filters.disciplineIds?.length;
      if (narrowed && self.currentBuildingId) {
        yield self.loadIssues(self.currentBuildingId);
      }
    }),

    updateIssueOptimistic(issueId: number, data: Partial<PMIssue>) {
      self.issues = self.issues.map((i) => (i.id === issueId ? { ...i, ...data } : i));
      // WHA-3448: mirror the patch onto the open expanded view so an edit
      // made in one surface shows instantly in the other.
      if (self.selectedIssue?.id === issueId) {
        self.selectedIssue = { ...self.selectedIssue, ...data };
      }
    },

    bulkUpdate: flow(function* bulkUpdate(
      issueIds: number[],
      data: Partial<PMIssue>,
      buildingId: number
    ) {
      // Optimistic — patch each issue in the in-memory list so the UI
      // reflects the change immediately. If the network call fails we
      // reload from the server and write the result back so the UI
      // actually rolls back to the server-authoritative state
      // (WHA-3337).
      const idSet = new Set(issueIds);
      // Captured before the optimistic patch overwrites them. Keyed by id rather than
      // collected as a list, because the emit below counts ISSUES CHANGED and the store
      // holds only what the scope loaded: a selection reaching past it (a realtime burst, a
      // stale board, a scope still paging) would otherwise report fewer events than rows.
      const fromById = new Map(self.issues.map((i) => [i.id, i.status]));
      self.issues = self.issues.map((i) => (idSet.has(i.id) ? { ...i, ...data } : i));
      // WHA-3448: mirror the patch onto the open expanded view.
      if (self.selectedIssue && idSet.has(self.selectedIssue.id)) {
        self.selectedIssue = { ...self.selectedIssue, ...data };
      }
      try {
        yield pmRepo.bulkUpdateIssues(issueIds, data);
        const to = data.status;
        if (to) {
          for (const id of issueIds) {
            const from = fromById.get(id) ?? "unknown";
            if (from !== to) {
              track("issue_status_changed", {
                from_status: from,
                to_status: to,
                bulk: issueIds.length > 1,
              });
            }
          }
        }
        if (writeEscapesFilters(data, self.filters)) {
          yield self.loadIssues(buildingId);
        }
      } catch (e) {
        // Re-read the whole scope so the UI rolls back to server-authoritative
        // state — and keeps every row it had, rather than shrinking to a page.
        // The reload clears `error` on the way in, so the failure is recorded
        // after it, not before.
        yield self.loadIssues(buildingId);
        self.error = failureMessage(e, "Bulk update failed");
        throw e;
      }
    }),

    bulkSetAssignees: flow(function* bulkSetAssignees(issueIds: number[], userIds: number[]) {
      // No bulk RPC for junction tables; replace per-issue in parallel.
      yield Promise.all(issueIds.map((id) => pmRepo.setIssueAssignees(id, userIds)));
    }),

    bulkSetDisciplines: flow(function* bulkSetDisciplines(
      issueIds: number[],
      disciplineIds: number[]
    ) {
      yield Promise.all(issueIds.map((id) => pmRepo.setIssueDisciplines(id, disciplineIds)));
    }),

    bulkDelete: flow(function* bulkDelete(issueIds: number[]) {
      yield Promise.all(issueIds.map((id) => pmRepo.softDeleteIssue(id)));
      const idSet = new Set(issueIds);
      self.issues = self.issues.filter((i) => !idSet.has(i.id));
    }),

    addComment: flow(function* addComment(
      issueId: number,
      authorId: number,
      body: string,
      mentionedUserIds: number[] = [],
      documentIds: number[] = []
    ) {
      yield pmRepo.createComment(
        { issue_id: issueId, author_id: authorId, body },
        mentionedUserIds,
        documentIds
      );
      // Before the reload: the comment is written by the line above, and a thread refresh
      // that throws would otherwise lose the event for a comment that exists.
      track("issue_comment_added", {
        has_mention: mentionedUserIds.length > 0,
        has_attachment: documentIds.length > 0,
      });
      self.selectedIssueComments = yield pmRepo.listComments(issueId);
    }),

    // WHA-3453: edit an existing comment's body, then reload the thread.
    editComment: flow(function* editComment(
      commentId: number,
      issueId: number,
      body: string,
      modifiedBy: number,
      mentionedUserIds: number[] = []
    ) {
      yield pmRepo.updateComment(commentId, body, modifiedBy, mentionedUserIds);
      self.selectedIssueComments = yield pmRepo.listComments(issueId);
    }),

    subscribe(buildingId: number, reloadFn: () => void) {
      self.channel?.unsubscribe();
      self.resyncCoalescer?.cancel();
      // Every event asks for the whole scope again, so a burst is coalesced
      // into a leading run plus at most one more per window.
      self.resyncCoalescer = createCoalescer(reloadFn, ISSUE_RESYNC_WINDOW_MS);
      self.channel = pmRepo.subscribeToIssues(buildingId, () => self.resyncCoalescer?.call());
      self.checklistChannel?.unsubscribe();
      self.checklistChannel = pmRepo.subscribeToBuildingChecklists(buildingId, () =>
        self.resyncCoalescer?.call()
      );
    },

    unsubscribe() {
      self.channel?.unsubscribe();
      self.channel = null;
      self.checklistChannel?.unsubscribe();
      self.checklistChannel = null;
      self.resyncCoalescer?.cancel();
      self.resyncCoalescer = null;
    },
  }))
  .actions((self) => ({
    /**
     * A draft's issue now exists — recorded at once, not when the save ends, so
     * a reload that brings the issue in mid-save hides the draft instead of
     * drawing both, and a later failure keeps the retry key.
     */
    recordDraftIssueCreated(id: number, created: PMIssue) {
      self.drafts = self.drafts.map((d) =>
        d.id === id ? { ...d, createdIssueId: created.id, createdRow: created } : d
      );
    },
  }))
  .actions((self) => {
    function replaceDraft(id: number, patch: Partial<IssueDraft>) {
      self.drafts = self.drafts.map((d) => (d.id === id ? { ...d, ...patch } : d));
    }

    function removeDraft(id: number) {
      self.drafts = self.drafts.filter((d) => d.id !== id);
      if (self.activeDraftId === id) self.activeDraftId = null;
    }

    /**
     * The new issue, put where its draft was drawn, in the same action that
     * removes the draft — so no frame shows neither, or both. The row is the
     * inserted one with the draft's chips over it; the reload that follows
     * replaces it with the server's copy. A reload that got there first wins.
     */
    function swapDraftForIssue(draft: IssueDraft, issueId: number, created: PMIssue | null) {
      if (created && self.currentBuildingId === draft.buildingId) {
        if (!self.issues.some((i) => i.id === issueId)) {
          const shown = projectStoredDraft(draft, self.draftLookups(draft.values.projectId));
          const row: PMIssue = {
            ...shown,
            ...created,
            // The fields as saved now: a retry rewrites them on the existing
            // row (see createIssueWithAssociations), so the inserted copy may
            // be older than what the person last typed.
            title: draft.values.title.trim(),
            status: draft.values.status,
            start_date: shown.start_date,
            due_date: shown.due_date,
            building_project_id: shown.building_project_id,
            parent_issue_id: created.parent_issue_id ?? shown.parent_issue_id,
            assignees: shown.assignees,
            disciplines: shown.disciplines,
            checklist_total: shown.checklist_total,
            checklist_done: 0,
            comments_count: 0,
            linked_documents: [],
            linked_elements: [],
          };
          // Only where the list the store holds would hold it: the page that
          // started the save may be gone, and the list now loaded may be My
          // Issues' or one filtered on something the issue does not match.
          // The reload that follows decides for anything the row cannot answer.
          if (newIssueMatchesFilters(row, self.filters)) {
            self.issues = [row, ...self.issues];
          }
        }
      }
      settle(draft, issueId);
      removeDraft(draft.id);
    }

    /** Record what became of a draft, for uploads that land after it has gone. */
    function settle(draft: IssueDraft, issueId: number | null) {
      const late = self.draftLateUploads.get(draft.id) ?? [];
      self.draftLateUploads.delete(draft.id);
      self.settledDrafts.set(draft.id, {
        issueId,
        createdBy: draft.createdBy,
        handled: new Set([
          ...draft.values.stagedAttachments.map((a) => a.id),
          ...draft.uploadedDocumentIds,
          ...late.map((a) => a.id),
        ]),
      });
    }

    /** Link (or bin) uploads that landed while the draft was saving or after it went. */
    const placeLateUploads = flow(function* placeLateUploads(
      issueId: number | null,
      createdBy: number | null,
      uploads: readonly StagedAttachment[]
    ) {
      for (const doc of uploads) {
        try {
          if (issueId != null) {
            yield pmRepo.addIssueDocument(issueId, doc.id);
          } else if (createdBy != null) {
            yield binDocument(doc.id, createdBy);
          }
        } catch (err) {
          // Left in "Issue Attachments" — visible, and the person can link or bin it.
          console.error("[pm] Failed to place an upload that landed after its draft", doc, err);
        }
      }
    });

    /**
     * An edit for a draft that cannot take it — being saved, or gone. Only one
     * kind matters: a document an upload has just created. It is kept for the
     * save to link, linked to the issue the draft became, or binned with the
     * discarded draft, rather than orphaned in "Issue Attachments".
     */
    function adoptLateUploads(
      id: number,
      draft: IssueDraft | null,
      patch: Partial<IssueDraftValues>
    ) {
      const uploaded = (patch.stagedAttachments ?? []).filter((a) => a.uploaded);
      if (uploaded.length === 0) return;
      if (draft) {
        const known = new Set([
          ...draft.values.stagedAttachments.map((a) => a.id),
          ...(self.draftLateUploads.get(id) ?? []).map((a) => a.id),
        ]);
        const fresh = uploaded.filter((a) => !known.has(a.id));
        if (fresh.length > 0) {
          self.draftLateUploads.set(id, [...(self.draftLateUploads.get(id) ?? []), ...fresh]);
        }
        return;
      }
      const settled = self.settledDrafts.get(id);
      if (!settled) return;
      const fresh = uploaded.filter((a) => !settled.handled.has(a.id));
      for (const a of fresh) settled.handled.add(a.id);
      if (fresh.length > 0) void placeLateUploads(settled.issueId, settled.createdBy, fresh);
    }

    const runCommit = flow(function* runCommit(id: number) {
      const draft = self.findDraft(id);
      if (!draft) return { ok: false, error: new Error(`No draft ${id}`) } as DraftCommitResult;
      if (draftTitleIsEmpty(draft.values) || draft.values.projectId == null) {
        // Not a failed save — nothing was attempted — so the draft stays as it was.
        return { ok: false, error: new DraftUntitledError() } as DraftCommitResult;
      }
      replaceDraft(id, { phase: "committing", error: null });
      if (self.activeDraftId === id) self.activeDraftId = null;
      const { values } = draft;
      const createdIssueIdRef = { current: draft.createdIssueId };
      const checklistSavedForRef = { current: draft.checklistSavedFor };
      let createdRow = draft.createdRow;
      try {
        const issueId: number = yield createIssueWithAssociations({
          issuesStore: self,
          buildingId: draft.buildingId,
          orgId: draft.orgId ?? undefined,
          userProfileId: draft.createdBy ?? undefined,
          title: values.title.trim(),
          status: values.status,
          startDate: values.startDate,
          dueDate: values.dueDate,
          projectId: String(values.projectId),
          assigneeIds: values.assigneeIds,
          disciplineIds: values.disciplineIds,
          parentIssueId: values.parentIssueId,
          stagedAttachments: values.stagedAttachments,
          checklistItems: values.checklistItems ?? [],
          checklistSavedForRef,
          // A draft is never raised from a model element, so there is no view to save.
          saveViewpoint: false,
          createdIssueIdRef,
          onViewpointSaveFailed: () => undefined,
          onIssueCreated: (row) => {
            createdRow = row;
            self.recordDraftIssueCreated(id, row);
          },
          analyticsSource: "issues_draft",
        });
        const late = self.draftLateUploads.get(id) ?? [];
        swapDraftForIssue(self.findDraft(id) ?? draft, issueId, createdRow);
        yield placeLateUploads(issueId, draft.createdBy, late);
        if (self.currentBuildingId === draft.buildingId) {
          yield self.loadIssues(draft.buildingId);
        }
        return { ok: true, issueId } as DraftCommitResult;
      } catch (error) {
        // Kept with its retry keys: the issue may already exist, and a retry
        // must finish that one rather than file a second. Uploads that landed
        // during the save join it again.
        const late = self.draftLateUploads.get(id) ?? [];
        self.draftLateUploads.delete(id);
        replaceDraft(id, {
          phase: "failed",
          error,
          createdIssueId: createdIssueIdRef.current,
          createdRow,
          checklistSavedFor: checklistSavedForRef.current,
          values: { ...values, stagedAttachments: [...values.stagedAttachments, ...late] },
          uploadedDocumentIds: [...draft.uploadedDocumentIds, ...late.map((a) => a.id)],
        });
        // Not reopened here: the save outlives the page, and the selection is
        // shared with My Issues, the 3D viewer and the drawing compare —
        // taking it would close whatever the person has open there. The
        // /issues page reopens a failed draft when it is the one showing.
        return { ok: false, error } as DraftCommitResult;
      }
    });

    /** Bin what was uploaded for a draft that is going away. Best-effort. */
    const binDraftUploads = flow(function* binDraftUploads(draft: IssueDraft) {
      if (draft.createdBy == null) return;
      for (const documentId of draft.uploadedDocumentIds) {
        try {
          yield binDocument(documentId, draft.createdBy);
        } catch (err) {
          // Left in "Issue Attachments" — visible, and the person can bin it.
          console.error("[pm] Failed to bin a discarded draft's upload", documentId, err);
        }
      }
    });

    return {
      /**
       * Open a new draft in the panel and return its id. Closes any open issue.
       * Leaving the previous draft (commit, discard or ask) is the caller's
       * job and must come first: with a draft already open this does nothing
       * and returns that draft's id.
       */
      startDraft(params: StartDraftParams): number {
        const active = self.activeDraft;
        if (active) {
          if (active.buildingId === params.buildingId) return active.id;
          // Left open on another building (the page went while it held input
          // but no description): it waits there, out of the panel.
          self.activeDraftId = null;
        }
        self.clearSelection();
        const values = initialDraftValues(params.defaults);
        const id = -++self.draftSeq;
        self.drafts = [
          ...self.drafts,
          {
            id,
            buildingId: params.buildingId,
            orgId: params.orgId ?? null,
            createdBy: params.userProfileId ?? null,
            createdAt: new Date().toISOString(),
            values,
            initial: values,
            phase: "editing",
            error: null,
            createdIssueId: null,
            createdRow: null,
            checklistSavedFor: null,
            uploadedDocumentIds: [],
            uploading: false,
          },
        ];
        self.activeDraftId = id;
        return id;
      },

      /**
       * Apply an edit to the open draft. A draft being saved cannot change; an
       * upload that lands then (or after the draft has gone) is still placed —
       * see adoptLateUploads. A `correction` moves the draft's defaults with
       * it, so the form fixing up a default is not counted as input.
       */
      updateDraft(
        patch: Partial<IssueDraftValues>,
        id: number | null = self.activeDraftId,
        meta: IssueDraftChangeMeta = {}
      ) {
        if (id == null) return;
        const draft = self.findDraft(id);
        if (!draft || draft.phase === "committing") {
          adoptLateUploads(id, draft, patch);
          return;
        }
        const values = { ...draft.values, ...patch };
        const initial = meta.correction
          ? correctDraftDefaults(draft.initial, draft.values, patch)
          : draft.initial;
        const uploaded = new Set(draft.uploadedDocumentIds);
        for (const a of patch.stagedAttachments ?? []) if (a.uploaded) uploaded.add(a.id);
        replaceDraft(draft.id, { values, initial, uploadedDocumentIds: [...uploaded] });
      },

      /** The panel's upload state — leaving waits while one is running. */
      setDraftUploading(id: number, uploading: boolean) {
        const draft = self.findDraft(id);
        if (draft && draft.uploading !== uploading) replaceDraft(id, { uploading });
      },

      /**
       * Put a waiting draft (one whose save failed) back in the panel. Refused
       * while another draft is open — leave that one first.
       */
      reopenDraft(id: number): boolean {
        const draft = self.findDraft(id);
        if (!draft || draft.phase === "committing") return false;
        if (self.activeDraftId != null && self.activeDraftId !== id) return false;
        self.clearSelection();
        self.activeDraftId = id;
        replaceDraft(id, { phase: "editing" });
        return true;
      },

      /**
       * Take the open draft out of the panel without saving or dropping it: it
       * waits, as a draft left on another building does, until its own page
       * (its building, scoped to its project) reopens it.
       */
      parkDraft(id: number) {
        if (self.activeDraftId === id) self.activeDraftId = null;
      },

      /**
       * Drop a draft, and bin the documents uploaded for it (picked ones are
       * left alone). Not while it is being saved. A draft whose issue already
       * exists — a save that failed after the insert — takes that issue with
       * it (soft-deleted, as the bin does), since the person was told the issue
       * is discarded. If that delete fails the draft is kept, `failed`, so the
       * issue is never left behind unexplained; the caller says so.
       */
      discardDraft: flow(function* discardDraft(id: number | null = self.activeDraftId) {
        const draft = id != null ? self.findDraft(id) : null;
        if (!draft || draft.phase === "committing") return false;
        if (draft.createdIssueId != null) {
          // Out of the panel and frozen while the delete runs, as a save is.
          replaceDraft(draft.id, { phase: "committing" });
          if (self.activeDraftId === draft.id) self.activeDraftId = null;
          try {
            yield pmRepo.softDeleteIssue(draft.createdIssueId);
          } catch (error) {
            // Uploads that landed during the delete stay with the draft.
            const late = self.draftLateUploads.get(draft.id) ?? [];
            self.draftLateUploads.delete(draft.id);
            replaceDraft(draft.id, {
              phase: "failed",
              error,
              values: {
                ...draft.values,
                stagedAttachments: [...draft.values.stagedAttachments, ...late],
              },
              uploadedDocumentIds: [...draft.uploadedDocumentIds, ...late.map((a) => a.id)],
            });
            return false;
          }
        }
        const late = self.draftLateUploads.get(draft.id) ?? [];
        settle(draft, null);
        removeDraft(draft.id);
        yield binDraftUploads(draft);
        yield placeLateUploads(null, draft.createdBy, late);
        if (draft.createdIssueId != null && self.currentBuildingId === draft.buildingId) {
          yield self.loadIssues(draft.buildingId);
        }
        return true;
      }),

      /**
       * Save a draft: the create sequence, then the issue swapped in where the
       * draft was drawn, then a reload. Runs here rather than in a component so
       * leaving the page does not cut it short. Never throws; on failure the
       * draft is kept (retry keys and all), marked `failed`, and reopened in the
       * panel unless another draft is open. A draft without a title is refused
       * with `DraftUntitledError` and left untouched. Calling it again while a
       * save is running joins that save.
       */
      commitDraft(id: number | null = self.activeDraftId): Promise<DraftCommitResult> {
        if (id == null) {
          return Promise.resolve({ ok: false, error: new Error("No draft is open") });
        }
        const running = self.draftCommits.get(id);
        if (running) return running;
        const commit = runCommit(id).finally(() => {
          self.draftCommits.delete(id);
        });
        self.draftCommits.set(id, commit);
        return commit;
      },
    };
  });

export type IssuesStoreInstance = Instance<typeof IssuesStore>;

function holdEdit(
  held: Map<number, Partial<PMIssue>>,
  issueId: number,
  patch: Partial<PMIssue>
): void {
  held.set(issueId, { ...held.get(issueId), ...patch });
}

function releaseEdit(
  held: Map<number, Partial<PMIssue>>,
  issueId: number,
  patch: Partial<PMIssue>
): void {
  const current = held.get(issueId);
  if (!current) return;
  const next: Partial<PMIssue> = { ...current };
  for (const key of Object.keys(patch) as (keyof PMIssue)[]) {
    if (Object.is(next[key], patch[key])) delete next[key];
  }
  if (Object.keys(next).length === 0) held.delete(issueId);
  else held.set(issueId, next);
}
